import { Component } from '@theme/component'
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantSelectedOptionsChangedEvent,
  VariantUpdateEvent,
} from '@theme/events'
import { morph } from '@theme/morph'
import { ProductFormComponent } from '@theme/product-form'

/**
 * A custom element that manages a variant picker.
 *
 * @typedef {Object} VariantPickerRefs
 * @property {HTMLElement[]} variantOptions - The variant options.
 *
 * @extends {Component<VariantPickerRefs>}
 */
export default class VariantPicker extends Component {
  requiredRefs = ['variantOptions']

  /** @type {string | undefined} */
  #pendingRequestUrl

  /** @type {AbortController | undefined} */
  #abortController

  /** @type {NodeListOf<ProductFormComponent> | null} */
  #productFormComponents

  /** @type {HTMLOptionElement[] } */
  #options

  /** @type {(string|null)[] } */
  #selectedOptions = []

  /** @type {{id: string, options: string[], option1: string, option2: string}[]} */
  #productVariants = []

  /** @type {boolean} */
  #isInitialized = false

  /** @type {number | null} */
  #fetchDebounceTimer = null

  /** @type {number} */
  #fetchDebounceDelayMs = 150

  /** @type {number} */
  #fetchRequestToken = 0

  #option1InputsSelector = `[data-option="option1"] input`
  #option2InputsSelector = `[data-option="option2"] input`

  connectedCallback() {
    super.connectedCallback()

    // Prevent duplicate initialization
    if (this.#isInitialized) {
      return
    }
    this.#isInitialized = true

    const timeout = setTimeout(() => {
      this.#productFormComponents = this.dataset.productFormSelector
        ? document.querySelectorAll(this.dataset.productFormSelector)
        : null
      this.#options = Array.from(
        this.querySelectorAll('select.js-main-product-variants option')
      )

      try {
        this.#productVariants = JSON.parse(
          this.querySelector('script.product-variants')?.textContent ?? '[]'
        )
      } catch {
        this.#productVariants = []
      }

      this.setSelectedOptions()
      this.updateOptionsStates()
      this.handleOOS()
      clearTimeout(timeout)
    }, 0)

    // Add event listeners only once
    if (!this.hasAttribute('data-listeners-added')) {
      this.addEventListener('change', this.variantChanged)
      window.addEventListener(
        ThemeEvents.variantSelectedOptionsChanged,
        this.handleSelectedOptionsChanged
      )
      this.setAttribute('data-listeners-added', 'true')
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    // Remove event listeners
    this.removeEventListener('change', this.variantChanged)
    window.removeEventListener(
      ThemeEvents.variantSelectedOptionsChanged,
      this.handleSelectedOptionsChanged
    )

    // Reset initialization state
    this.#isInitialized = false
    this.removeAttribute('data-listeners-added')

    if (this.#fetchDebounceTimer) {
      window.clearTimeout(this.#fetchDebounceTimer)
      this.#fetchDebounceTimer = null
    }
  }

  setSelectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    this.#selectedOptions = fieldsets.map((fieldset) => {
      /** @type {HTMLInputElement | null} */
      const selectedOption = fieldset.querySelector('input:checked')
      return selectedOption?.value ?? null
    })
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return

    const selectedElement = event.target

    this.updateSelectedOption(event.target)
    const { optionValueId } = event.target.dataset
    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId ?? '' }))

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog')

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0]
    const newUrl = event.target.dataset.connectedProductUrl
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl

    let variantId = ''
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio'
    ) {
      variantId = event.target.dataset.variantId || ''
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex]
      variantId = selectedOption?.dataset.variantId || ''
    }

    // Debounce fetch to batch rapid selections
    this.#fetchRequestToken += 1
    const requestToken = this.#fetchRequestToken

    if (this.#fetchDebounceTimer) {
      window.clearTimeout(this.#fetchDebounceTimer)
      this.#fetchDebounceTimer = null
    }

    this.#fetchDebounceTimer = window.setTimeout(() => {
      this.#fetchDebounceTimer = null

      this.fetchUpdatedSection(
        this.buildRequestUrl(selectedElement),
        loadsNewProduct,
        optionValueId,
        variantId
      ).then(() => {
        if (requestToken !== this.#fetchRequestToken) {
          return
        }

        const url = new URL(window.location.href)

        /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
        const selectedOptions = this.querySelectorAll(
          `input[checked], option[selected]`
        )

        if (isOnProductPage) {
          if (variantId) {
            const allOptionsSelected =
              selectedOptions.length === this.refs.variantOptions.length

            if (allOptionsSelected) {
              url.searchParams.set('variant', variantId)
            }
          } else {
            url.searchParams.delete('variant')
          }
        }

        this.setSelectedOptions()
        this.updateOptionsStates()
        this.handleOOS()

        const optionsIds = Array.from(selectedOptions)
          .map((option) => option.dataset.optionValueId)
          .filter(Boolean)
        window.dispatchEvent(
          new VariantSelectedOptionsChangedEvent(optionsIds, this)
        )

        // Change the path if the option is connected to another product via combined listing.
        if (loadsNewProduct && newUrl) {
          url.pathname = newUrl
        }

        if (url.href !== window.location.href) {
          history.replaceState({}, '', url.toString())
        }
      })
    }, this.#fetchDebounceDelayMs)
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      )

      if (!targetElement) throw new Error('Target element not found')

      target = targetElement
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      )

      if (!newSelectedOption) throw new Error('Option not found')

      for (const option of target.options) {
        option.removeAttribute('selected')
      }

      newSelectedOption.setAttribute('selected', 'selected')
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl
    this.#pendingRequestUrl = productUrl
    const params = []

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`)
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`)
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`)
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0]
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        '&'
      )}`
    }
    return `${productUrl}?${params.join('&')}`
  }

  /**
   * Disables or enables buttons.
   * @param {boolean} disabled - If the buttons should be disabled.
   */
  #setButtonsDisabled(disabled) {
    const buttons = this.querySelectorAll('[data-connected-product-url]')
    buttons.forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = disabled
      }
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false')
    })
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {string | undefined} optionValueId - The option value ID.
   * @param {string | undefined} variantId - The variant ID.
   */
  fetchUpdatedSection(
    requestUrl,
    shouldMorphMain = false,
    optionValueId = '',
    variantId = ''
  ) {
   

    // Cancel previous request if it exists
    this.#abortController?.abort()
    this.#abortController = new AbortController()

    return fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer')

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`
        )?.textContent

        if (!textContent) {
          this.#setButtonsDisabled(false)
          return
        }

        let newProduct = null
        if (shouldMorphMain) {
          this.updateMain(html)
        } else {
          newProduct = this.updateVariantPicker(html)
        }

        // Update selected options FIRST so selectedOptionId works correctly
        const selectedOptions = this.updateSelectedOptions(optionValueId)
        this.setSelectedOptions()

        // Now dispatch the event after DOM is updated
        if (!shouldMorphMain && this.selectedOptionId) {
          const variantUpdateEvent = new VariantUpdateEvent(
            JSON.parse(textContent),
            this.selectedOptionId,
            {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            }
          )
          this.dispatchEvent(variantUpdateEvent)
        }

        const productFormComponentContent =
          this.getProductFormComponentContent(html)

        if (productFormComponentContent) {
          const { addToCartText, canAddToCart } = productFormComponentContent
          this.updateProductFormComponents(
            selectedOptions,
            addToCartText,
            canAddToCart,
            variantId
          )

          const allOptionsSelected =
            selectedOptions.length === this.refs.variantOptions.length

          // Dispatch event for inventory visibility
          document.dispatchEvent(
            new CustomEvent('variantSelectionUpdate', {
              detail: {
                allOptionsSelected,
                productId: this.dataset.productId,
              },
            })
          )
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user')
        } else {
          console.error(error)
        }
      })
      .finally(() => {
        // unlock buttons
        this.#setButtonsDisabled(false)
      })
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    )

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found')
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId
      const newProductUrl = newVariantPickerSource.dataset.productUrl

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl }
      }

      this.dataset.productId = newProductId
      this.dataset.productUrl = newProductUrl
    }

    morph(this, newVariantPickerSource)

    return newProduct
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main')
    const newMain = newHtml.querySelector('main')

    if (!main || !newMain) {
      throw new Error('No new main source found')
    }

    morph(main, newMain)
  }

  /**
   * Updates the selected options.
   * @param {string | undefined} optionValueId - The option value ID.
   */
  updateSelectedOptions(optionValueId) {
    let selector = `input[checked], option[selected]`
    if (optionValueId) {
      selector = `[data-option-value-id="${optionValueId}"], ${selector}`
    }

    /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
    const selectedOptions = this.querySelectorAll(selector)
    if (selectedOptions.length) {
      selectedOptions.forEach((option) => {
        if (option instanceof HTMLInputElement) {
          option.checked = true
        } else if (option instanceof HTMLOptionElement) {
          option.selected = true
        }
      })
    }

    return selectedOptions
  }

  /**
   * Gets the product form component content.
   * @param {Document} sectionHtml - The section HTML.
   * @returns {ProductFormComponentContent | null} The product form component content.
   * @typedef {Object} ProductFormComponentContent
   * @property {string} addToCartText - The add to cart text.
   * @property {boolean} canAddToCart - If the product can be added to the cart.
   */
  getProductFormComponentContent(sectionHtml) {
    const productFormComponent = sectionHtml.querySelector(
      'product-form-component'
    )
    if (!productFormComponent) {
      return null
    }

    return {
      addToCartText:
        productFormComponent.getAttribute('data-add-to-cart-text') ?? '',
      canAddToCart:
        productFormComponent.getAttribute('data-can-add-to-cart') === 'true',
    }
  }

  /**
   * Updates the product form component.
   * @param {NodeListOf<HTMLInputElement | HTMLOptionElement>} selectedOptions - The selected options.
   * @param {string} addToCartText - The add to cart text.
   * @param {boolean} canAddToCart - If the product can be added to the cart.
   */
  updateProductFormComponents(
    selectedOptions,
    addToCartText,
    canAddToCart,
    variantId = ''
  ) {
    if (!this.#productFormComponents?.length) {
      return
    }

    this.#productFormComponents.forEach((productFormComponent) => {
      const { addToCartButtonContainer } = productFormComponent.refs
      if (!addToCartButtonContainer) {
        return
      }

      const allOptionsSelected =
        selectedOptions.length === this.refs.variantOptions.length
      const allAddToCartButtons = [
        ...Array.from(
          document.querySelectorAll('.sticky-atc__add-to-cart-button')
        ),
        ...(addToCartButtonContainer?.refs.addToCartButton
          ? [addToCartButtonContainer.refs.addToCartButton]
          : []),
      ]
      allAddToCartButtons.forEach((button) => {
        if (!allOptionsSelected) {
          button.disabled = true
          button.textContent = Theme.translations.selectASize || 'SELECT A SIZE'
          return
        }
        button.disabled = !canAddToCart
        button.textContent = addToCartText
      })
      productFormComponent.refs.variantId.value = variantId

      // Show/hide pickup availability based on canAddToCart and selection completeness
      try {
        const localPickup = productFormComponent.querySelector('local-pickup')
        if (localPickup) {
          if (canAddToCart && allOptionsSelected) {
            localPickup.removeAttribute('hidden')
          } else {
            localPickup.setAttribute('hidden', '')
          }
        }
      } catch (error) {
        // Log error to aid debugging; consider removing in production if desired
        console.error('Error updating local pickup availability:', error)
      }
    })
  }

  /**
   * Handles the variant picker selected options changed event.
   * @param {VariantSelectedOptionsChangedEvent} event - The variant picker selected options changed event.
   */
  handleSelectedOptionsChanged = (event) => {
    const { optionsIds, source } = event.detail
    if (
      source === this ||
      !optionsIds.length ||
      (!this.#productFormComponents &&
        !this.contains(this.#productFormComponents))
    ) {
      return
    }

    const sourceOptions = source.querySelectorAll('input, option')
    const thisElementOptions = this.querySelectorAll('input, option')
    sourceOptions.forEach((sourceOption, index) => {
      const elementOption = thisElementOptions[index]
      if (!elementOption) {
        return
      }

      elementOption.setAttribute(
        'data-option-value-id',
        sourceOption.getAttribute('data-option-value-id') ?? ''
      )
      elementOption.setAttribute(
        'data-variant-id',
        sourceOption.getAttribute('data-variant-id') ?? ''
      )
      elementOption.setAttribute(
        'data-connected-product-url',
        sourceOption.getAttribute('data-connected-product-url') ?? ''
      )

      if (
        elementOption instanceof HTMLOptionElement &&
        sourceOption instanceof HTMLOptionElement
      ) {
        elementOption.value = sourceOption.value
        elementOption.selected = sourceOption.selected
      } else if (
        elementOption instanceof HTMLInputElement &&
        sourceOption instanceof HTMLInputElement
      ) {
        elementOption.setAttribute(
          'aria-disabled',
          sourceOption.getAttribute('aria-disabled') ?? ''
        )
        elementOption.setAttribute(
          'data-variant-oos',
          sourceOption.getAttribute('data-variant-oos') ?? ''
        )
        elementOption.setAttribute(
          'aria-label',
          sourceOption.getAttribute('aria-label') ?? ''
        )
        elementOption.value = sourceOption.value
        elementOption.checked = sourceOption.checked
      }
    })
  }

  updateOptionsStates() {
    if (!this.#options.length) {
      return
    }

    const allOption1s = [
      ...new Set(this.#options.map(({ dataset }) => dataset.option1)),
    ]
    const allOption2s = [
      ...new Set(this.#options.map(({ dataset }) => dataset.option2)),
    ]

    this.#productVariants.map((variant) => {
      const oosOption1s = this.#options
        .filter(
          (el) =>
            el.dataset.option2 === variant.option2 &&
            el.dataset.variantOos === 'true'
        )
        .map((el) => el.dataset.option1)
      const oosOption2s = this.#options
        .filter(
          (el) =>
            el.dataset.option1 === variant.option1 &&
            el.dataset.variantOos === 'true'
        )
        .map((el) => el.dataset.option2)

      const availableOption1s = this.#options
        .filter((el) => el.dataset.option2 === variant.option2)
        .map((el) => el.dataset.option1)
      const availableOption2s = this.#options
        .filter((el) => el.dataset.option1 === variant.option1)
        .map((el) => el.dataset.option2)

      const notOosOption1s = allOption2s.filter(
        (option) => !oosOption1s.includes(option)
      )
      const notOosOption2s = allOption2s.filter(
        (option) => !oosOption2s.includes(option)
      )

      const unavailableOption1s = notOosOption1s.filter(
        (option) => !availableOption1s.includes(option)
      )
      const unavailableOption2s = notOosOption2s.filter(
        (option) => !availableOption2s.includes(option)
      )

      const totalUnattainableOption1s = [
        ...new Set(oosOption1s.concat(unavailableOption1s)),
      ]
      const totalUnattainableOption2s = [
        ...new Set(oosOption2s.concat(unavailableOption2s)),
      ]

      /** @type {HTMLInputElement|null} */
      const option2Input = this.querySelector(
        `${this.#option2InputsSelector}[value="${variant.option2}"]`
      )
      if (option2Input) {
        let ariaDisabled = false
        let dataUnavailable = false

        // for a given option, if ALL its corresponding options are either OOS or do not exist, mark the given option as 'OOS'
        if (totalUnattainableOption1s.length === allOption1s.length) {
          ariaDisabled = true
        }

        // for a given option, if ALL its corresponding options do not exist, mark the given option as 'Unavailable'
        if (unavailableOption1s.length === allOption1s.length) {
          dataUnavailable = true
        }

        this.updateOptionState({
          option: option2Input,
          ariaDisabled,
          dataUnavailable,
        })
      }

      /** @type {HTMLInputElement|null} */
      const option1Input = this.querySelector(
        `${this.#option1InputsSelector}[value="${variant.option1}"]`
      )
      if (option1Input) {
        let ariaDisabled = false
        let dataUnavailable = false

        if (totalUnattainableOption2s.length === allOption2s.length) {
          ariaDisabled = true
        }

        if (unavailableOption2s.length === allOption2s.length) {
          dataUnavailable = true
        }

        this.updateOptionState({
          option: option1Input,
          ariaDisabled,
          dataUnavailable,
        })
      }
    })
  }

  /**
   * Handles the out of stock variants.
   */
  handleOOS() {
    if (!this.#options.length) {
      return
    }

    /** @type {HTMLInputElement|null} */
    const option1 = this.querySelector(`${this.#option1InputsSelector}:checked`)
    const option1Value = option1?.value ?? null

    /** @type {HTMLInputElement|null} */
    const option2 = this.querySelector(`${this.#option2InputsSelector}:checked`)
    const option2Value = option2?.value ?? null

    if (this.#selectedOptions.length === 2) {
      // holding option 1 steady, what values of option 2 are OOS? ex if the user chose waist: 28, what values of length are OOS?
      const option1steady = this.#options
        .filter(
          (el) =>
            el.dataset.option1 === option1Value &&
            el.dataset.variantOos === 'true'
        )
        .map((el) => el.dataset.option2)
      // holding option 2 steady, what values of option 1 are OOS? ex if the user chose length: 30, what values of waist are OOS?
      const option2steady = this.#options
        .filter(
          (el) =>
            el.dataset.option2 === option2Value &&
            el.dataset.variantOos === 'true'
        )
        .map((el) => el.dataset.option1)

      // given the user chose waist: 28, what values of length EXIST
      const allOption1s = this.#options
        .filter((el) => el.dataset.option1 === option1Value)
        .map((el) => el.dataset.option2)
      // given the user chose length: 30, what values of waist EXIST
      const allOption2s = this.#options
        .filter((el) => el.dataset.option2 === option2Value)
        .map((el) => el.dataset.option1)

      if (option1Value) {
        this.querySelectorAll(this.#option2InputsSelector).forEach((item) => {
          if (!(item instanceof HTMLInputElement)) {
            return
          }

          let ariaDisabled = false
          let dataUnavailable = false

          // for all length selectors,
          if (option1steady.indexOf(item.value) > -1) {
            // if the length is in the OOS list, make it OOS
            ariaDisabled = true
            dataUnavailable = false
          } else if (allOption1s.indexOf(item.value) > -1) {
            // if the length is in the ALL LENGTHS THAT EXIST list, make it available
            ariaDisabled = false
            dataUnavailable = false
          } else {
            // if it's in neither list, it doesn't exist
            ariaDisabled = true
            dataUnavailable = true
          }

          this.updateOptionState({
            option: item,
            ariaDisabled,
            dataUnavailable,
          })
        })
      }

      if (option2Value) {
        this.querySelectorAll(this.#option1InputsSelector).forEach((item) => {
          if (!(item instanceof HTMLInputElement)) {
            return
          }

          let ariaDisabled = false
          let dataUnavailable = false

          if (option2steady.indexOf(item.value) > -1) {
            // item is oos
            ariaDisabled = true
            dataUnavailable = false
          } else if (allOption2s.indexOf(item.value) > -1) {
            ariaDisabled = false
            dataUnavailable = false
          } else {
            ariaDisabled = true
            dataUnavailable = true
          }

          this.updateOptionState({
            option: item,
            ariaDisabled,
            dataUnavailable,
          })
        })
      }
    } else {
      const option1OOS = this.#options
        .filter((el) => el.dataset.variantOos === 'true')
        .map((el) => el.dataset.option1)

      this.querySelectorAll(this.#option1InputsSelector).forEach((item) => {
        if (!(item instanceof HTMLInputElement)) {
          return
        }

        this.updateOptionState({
          option: item,
          ariaDisabled: option1OOS.indexOf(item.value) > -1,
        })
      })
    }
  }

  /**
   /**
    * Updates the state of an option.
    * @param {object} params - The parameters.
    * @param {HTMLInputElement} params.option - The option to update.
    * @param {boolean} params.ariaDisabled - The aria-disabled state.
    * @param {boolean} [params.dataUnavailable] - (Optional) The data-unavailable state.
    */
  updateOptionState({ option, ariaDisabled, dataUnavailable }) {
    option.setAttribute('aria-disabled', ariaDisabled.toString())

    if (this.dataset.disableOutOfStockVariantsSelection === 'true') {
      option.disabled = ariaDisabled
    }

    if (dataUnavailable !== undefined) {
      option.setAttribute('data-unavailable', dataUnavailable.toString())
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    )

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined
    }

    return selectedOption
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this
    if (!selectedOption) return undefined
    const { optionValueId } = selectedOption.dataset

    if (!optionValueId) {
      throw new Error('No option value ID found')
    }

    return optionValueId
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    )

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset

      if (!optionValueId) throw new Error('No option value ID found')

      return optionValueId
    })
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker)
}
